<section class="hero wave-bg">
  <div class="container hero-content hero-content--centered">
    <h1>
      Tierce Maintenance Applicative (TMA) & 
      <span class="accent"> MCO pour applications critiques</span>
    </h1>
    <p class="hero-description">
      Sécurisez votre business avec une maintenance proactive. Nos équipes d'experts en développement Node.js, Angular et Android assurent la stabilité, la sécurité et l'évolution de vos outils métiers 24h/24 et 7j/7.
    </p>
    <div class="cta-row">
       <a
        href="https://cal.mesh-box.fr/ogirodin/30min"
        target="_blank"
        rel="noopener"
        class="button primary large"
      >
        Sécuriser mon application
      </a>
      <a href="/simulateur-cii" class="button large outline">
        🏛️ Calculer mon crédit d'impôt →
      </a>
    </div>
    <div class="mt-6 flex justify-center">
      <app-cii-badge variant="compact" />
    </div>
  </div>
</section>

<section class="section">
  <div class="container">
    <app-cii-badge variant="table" />
  </div>
</section>

<section class="section alt">
  <div class="container max-w-4xl">
    <h2>Pourquoi choisir notre TMA spécialisée Node.js et Angular ?</h2>
    <p class="mb-6">
      La Tierce Maintenance Applicative (TMA) ne se limite pas à la simple correction de bugs. Chez Mesh Box, nous adoptons une approche holistique de la maintenance pour garantir que votre patrimoine logiciel reste un actif stratégique plutôt qu'une source de stress technique.
    </p>
    <h3>Une réactivité sans faille avec nos SLA (Service Level Agreements)</h3>
    <p class="mb-6">
      Nous comprenons que chaque minute d'indisponibilité de votre application métier se traduit par une perte directe de chiffre d'affaires. C'est pourquoi nous proposons des contrats de maintenance avec des engagements de délais d'intervention (GTI) et de rétablissement (GTR) personnalisés selon la criticité de vos processus. Notre équipe assure un monitoring continu pour anticiper les défaillances avant même qu'elles n'impactent vos utilisateurs finaux.
    </p>
    <h3>Optimisation continue et réduction de la dette technique</h3>
    <p>
      Contrairement à une maintenance curative classique, notre service de TMA inclut des phases de maintenance préventive et évolutive. Nous analysons régulièrement vos journaux d'erreurs, optimisons les requêtes SQL et mettons à jour les dépendances critiques (NPM, bibliothèques tierces) pour prévenir les failles de sécurité et les ralentissements de performance.
    </p>
  </div>
</section>

<section class="section">
  <div class="container">
    <h2 style="text-align: center; margin-bottom: 1rem;">Expertise technique ciblée par technologie</h2>
    <p style="text-align: center; margin-bottom: 3rem; color: var(--text-light);">
      Une maintenance de haute précision pour chaque brique de votre stack technologique.
    </p>
    <div class="grid-3">
      <div class="card highlight-hover">
        <div class="card-icon">⚙️</div>
        <h3>TMA Node.js & NestJS</h3>
        <p>Expertise pointue sur le runtime Node.js. Nous gérons les fuites de mémoire (memory leaks), l'optimisation de la boucle d'événements et la sécurisation de vos APIs REST ou GraphQL.</p>
        <a routerLink="/tma-nodejs" class="link-arrow">Expertise Node.js</a>
      </div>
      <div class="card highlight-hover">
        <div class="card-icon">🎨</div>
        <h3>TMA Angular & RxJS</h3>
        <p>Maintenez vos interfaces fluides et réactives. Nous maîtrisons les signaux Angular, la gestion d'état complexe et l'optimisation des performances de rendu (Core Web Vitals).</p>
        <a routerLink="/tma-angular" class="link-arrow">Expertise Angular</a>
      </div>
      <div class="card highlight-hover">
        <div class="card-icon">📱</div>
        <h3>TMA Android Native</h3>
        <p>Assurez la compatibilité de vos applications mobiles sur le parc Android. Correction de crashs spécifiques, optimisation de la consommation batterie et mises en conformité Play Store.</p>
        <a routerLink="/tma-android" class="link-arrow">Expertise Android</a>
      </div>
    </div>
  </div>
</section>

<section class="section alt">
  <div class="container">
    <h2>Pilotage et transparence : Le tableau de bord de votre maintenance</h2>
    <p class="mb-8">
      L'externalisation de votre maintenance logicielle ne doit pas être une "boîte noire". Nous mettons à votre disposition des rapports mensuels détaillés incluant le temps passé, les correctifs appliqués et les recommandations d'évolution.
    </p>
    <div class="grid-2">
      <div class="card">
          <h3>Monitoring & Observabilité</h3>
          <p>Utilisation d'outils industriels comme Sentry, Datadog ou Grafana pour une visibilité totale sur l'état de santé de vos serveurs et de vos applications.</p>
      </div>
      <div class="card">
          <h3>Sécurité & Conformité RGPD</h3>
          <p>Veille active sur les vulnérabilités CVE et application immédiate des correctifs de sécurité. Nous garantissons que votre code reste conforme aux exigences de protection des données.</p>
      </div>
    </div>
    <div class="mt-8 text-center">
      <p class="text-sm text-muted">Pour approfondir les enjeux de sécurité, consultez les recommandations de l'<a href="https://www.ssi.gouv.fr/" target="_blank" rel="noopener noreferrer" class="underline">ANSSI</a>.</p>
    </div>
  </div>
</section>

<section class="section">
  <div class="container max-w-4xl">
    <h2>L'avantage stratégique de la TMA pour votre DSI</h2>
    <p class="mb-6">
      En confiant la maintenance opérationnelle à Mesh Box, vous libérez vos ressources internes pour qu'elles se concentrent sur l'innovation et les nouveaux projets à forte valeur ajoutée. C'est le choix de l'efficacité opérationnelle alliée à une maîtrise parfaite des coûts de possession (TCO) de votre logiciel.
    </p>
    
    <div style="margin-top: 2rem; padding: 1.5rem; background: var(--card); border-radius: 8px; border-left: 4px solid var(--brand); box-shadow: var(--shadow);">
      <h3>Transition vers la modernisation</h3>
      <p>Votre application souffre d'un legacy trop lourd qui bloque toute évolution ? Parfois, la TMA seule ne suffit plus. Découvrez comment nous pouvons auditer et moderniser vos systèmes existants.</p>
      <a routerLink="/refonte-modernisation-legacy" style="color: var(--brand); font-weight: bold; text-decoration: none;">
        Audit et Modernisation Legacy →
      </a>
    </div>
  </div>
</section>

<!-- FAQ SECTION -->
<section class="section" style="background: var(--bg);">
  <div class="container">
    <h2>Questions Fréquentes sur notre Tierce Maintenance Applicative</h2>
    <div class="grid-2">
      <div class="card">
        <h4>Quels sont les niveaux de SLA proposés ?</h4>
        <p>Nos SLA sont modulables en fonction de vos besoins métier. Nous pouvons proposer des interventions en heures ouvrées ou une astreinte étendue pour les systèmes critiques nécessitant une haute disponibilité.</p>
      </div>
      <div class="card">
        <h4>Comment gérez-vous le transfert de compétences ?</h4>
        <p>Lors de la phase de transition (onboarding), nous effectuons un audit flash de votre code et documentons les processus clés pour garantir une reprise de connaissance efficace et sans risque.</p>
      </div>
    </div>
  </div>
</section>

<section class="section cta-final alt">
  <div class="container">
    <h2>Garantissez la pérennité de votre investissement logiciel</h2>
    <p>Ne laissez plus la maintenance devenir un frein à votre agilité. Discutons de votre besoin de MCO.</p>
    <a 
      href="https://cal.mesh-box.fr/ogirodin/30min"
      target="_blank"
      rel="noopener"
      class="button primary large" 
    >
      Obtenir une proposition de TMA
    </a>
  </div>
</section>